<template>
  <b-container fluid class="whatsapp-page py-3">
    <div class="d-flex align-items-center mb-2">
      <h4 class="mb-0 mr-3">{{ $t('whatsapp.title') }}</h4>
      <b-badge :variant="connected ? 'success' : 'warning'">
        {{ connected ? $t('whatsapp.live') : $t('whatsapp.reconnecting') }}
      </b-badge>
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-auto"
        @click="$refs.newConversationModal.open()"
      >
        {{ $t('whatsapp.template.new-conversation') }}
      </b-button>
    </div>

    <!-- Business-initiated conversations (M6): template message to a
         consented contact; the created thread is selected on acceptance. -->
    <template-send-modal ref="newConversationModal" @sent="onTemplateSent" />

    <b-alert
      :show="!!error"
      variant="danger"
      dismissible
      @dismissed="clearError"
    >
      {{ error }}
    </b-alert>

    <b-row class="whatsapp-panes border rounded no-gutters">
      <b-col
        cols="12"
        :md="store.selectedConversation ? 3 : 4"
        class="border-right h-100"
      >
        <conversation-list
          :conversations="store.conversations"
          :selected-id="store.selectedId"
          :total="store.conversationsTotal"
          :loading="store.conversationsLoading"
          @select="select"
          @refresh="refresh"
        />
      </b-col>
      <b-col cols="12" :md="store.selectedConversation ? 6 : 8" class="h-100">
        <message-thread
          :conversation="store.selectedConversation"
          :messages="store.messages"
          :has-older="store.hasOlderMessages"
          :loading="store.messagesLoading"
          @load-older="store.loadOlderMessages()"
        />
      </b-col>
      <b-col
        v-if="store.selectedConversation"
        cols="12"
        md="3"
        class="border-left h-100 d-none d-md-block"
      >
        <conversation-details-panel
          :conversation="store.selectedConversation"
        />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { getModule } from 'vuex-module-decorators';
import ConversationDetailsPanel from '@/components/whatsapp/ConversationDetailsPanel.vue';
import ConversationList from '@/components/whatsapp/ConversationList.vue';
import MessageThread from '@/components/whatsapp/MessageThread.vue';
import TemplateSendModal from '@/components/whatsapp/TemplateSendModal.vue';
import WhatsAppSocketMixin from '@/components/mixins/WhatsAppSocketMixin';
import { WhatsappModule } from '@/store/whatsapp.module';

/**
 * The WhatsApp inbox (GID-2697): conversation list, thread, composer (M3), and
 * the details panel (M5 — contact link, job links, internal notes). Whether a
 * reply can actually be sent is decided server-side (24h window, environment
 * config); the composer only mirrors that verdict.
 */
export default {
  name: 'WhatsApp',
  components: {
    ConversationDetailsPanel,
    ConversationList,
    MessageThread,
    TemplateSendModal,
  },
  mixins: [WhatsAppSocketMixin],
  data() {
    return { hasConnectedOnce: false };
  },
  computed: {
    store() {
      return getModule(WhatsappModule, this.$store);
    },
    connected() {
      return this.wsAuthenticated;
    },
    error() {
      return this.store.error;
    },
  },
  created() {
    this.store.fetchConversations();
  },
  methods: {
    select(id) {
      this.store.selectConversation(id);
    },
    refresh() {
      this.store.fetchConversations();
    },
    clearError() {
      this.store.setError(null);
    },
    async onTemplateSent(conversationId) {
      // The conversation was just created server-side; refresh the list so it
      // exists locally, then open it — the queued template appears via the WS.
      await this.store.fetchConversations();
      this.select(conversationId);
    },
    onWhatsAppEvent(event) {
      if (
        event.type !== 'whatsapp:message' &&
        event.type !== 'whatsapp:conversation'
      ) {
        return;
      }
      this.store.applyRealtimeEvent(event);
    },
    onWhatsAppSocketOpen() {
      // `created()` already loaded the list; only a RE-connect needs a resync,
      // because the socket may have been down long enough to miss events and
      // the REST endpoints are the source of truth.
      if (!this.hasConnectedOnce) {
        this.hasConnectedOnce = true;
        return;
      }
      this.store.fetchConversations();
      if (this.store.selectedId) {
        this.store.selectConversation(this.store.selectedId);
      }
    },
  },
};
</script>

<style scoped>
.whatsapp-panes {
  height: calc(100vh - 190px);
  min-height: 320px;
  overflow: hidden;
}
</style>
